import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../../services/authentication.service';
import { SapiService, SapiQueryResponse, SapiEntity } from '../../../services/sapi.service';
import { BbFilterConfiguration } from '../../shared/bb-filters/bb-filters.component';
import { BbTableService, TableRow } from '../../../services/bb-table.service';
import { FilterSetService } from '../../../services/filter-set.service';
import { Subscription } from 'rxjs';

export interface Language extends SapiEntity {
	isocode?: string;
	isdefault?: boolean;
	name?: string;
}

interface SystemConfig {
	'media-clipid': string;
}

interface System {
	label: string;
	priority: string;
	subtype: string;
	systemconfig: SystemConfig;
	type: string;
}

interface Position {
	type: string;
	name: string;
	system: string;
	'media-types': string[];
	systems: System[];
}

@Component({
	selector: 'playout-details-miscellaneous',
	templateUrl: './playout-details-miscellaneous.component.html',
	styleUrls: ['./playout-details-miscellaneous.component.scss'],
	providers: [
		// After navigating away the BbtableService needs to be destroyed
		BbTableService
	]
})

export class PlayoutDetailsMiscellaneousComponent implements OnChanges, OnDestroy {
	@Output() resetVideo: EventEmitter<any> = new EventEmitter<any>();

	@Input() playout: any;
	@Input() form: NgForm;
	@Input() previewPlayer: any;

	skinOptions: any[];
	defaultSubtitleOptions: any[];
	defaultAudiotrackOptions: any[];
	disableCookieOptions: any[];

	showLanguages: boolean;
	defaultLanguage: string;

	selectLeaderModel: any;

	filterConfig: BbFilterConfiguration;

	subscriptions: Subscription;

	rows: TableRow[] = [];

	leaderPositionId: string;
	initialLeaderPositionId: string;

	uploadServiceSubscription: Subscription;

	// TODO: Implement loading or remove from html
	loading: boolean;

	constructor(
		private translate: TranslateService,
		private authenticationService: AuthenticationService,
		private sapiService: SapiService,
		private filterSetService: FilterSetService,
		public bbTableService: BbTableService
	) {
		this.filterSetService.setLinkedToUrl(false);
		this.showLanguages = false;
		this.subscriptions = new Subscription();
		this.authenticationService.hasPermission('subtitles').subscribe((hasRight) => {
			if (hasRight) {
				this.showLanguages = true;
			}
		});
		this.authenticationService.hasPermission('audiotracks').subscribe((hasRight) => {
			if (hasRight) {
				this.showLanguages = true;
			}
		});

		this.sapiService.getUrl('/language').subscribe((languages: SapiQueryResponse) => {
			if (languages && languages.items && languages.items.length > 0) {
				languages.items.forEach((language: Language) => {
					if (language && language.isdefault) {
						this.defaultLanguage = (language.isocode ? this.translate.instant('LANGUAGE_' + language.isocode.toUpperCase()) : '');
					}
				});
			}

			this.setLanguageOptions();
		});

		this.disableCookieOptions = [
			{
				label: this.translate.instant('ENABLE_COOKIES'),
				value: 'false'
			},
			{
				label: this.translate.instant('ONLY_TRACKING'),
				value: 'tracking'
			},
			{
				label: this.translate.instant('DISABLE_COOKIES'),
				value: 'true'
			}
		];

		this.skinOptions = [
			{
				label: this.translate.instant('AUTO'),
				value: 'Auto'
			},
			{
				label: this.translate.instant('ALWAYS_ON'),
				value: 'Always_on'
			},
			{
				label: this.translate.instant('DISABLE_ON_TOUCH'),
				value: 'Disable_on_touch'
			},
			{
				label: this.translate.instant('DISABLE_ALWAYS'),
				value: 'Disable_always'
			}
		];
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.playout && simpleChanges.playout.currentValue) {
			const playout = simpleChanges.playout.currentValue;
			this.leaderPositionId = this.getLeaderMediaclipId(playout.positions);
			this.initialLeaderPositionId = this.leaderPositionId;
			this.selectLeaderMediaclip(this.leaderPositionId);
		}
	}

	ngOnDestroy() {
		if (this.uploadServiceSubscription) {
			this.uploadServiceSubscription.unsubscribe();
		}
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	loadCustomSkin(): void {
		this.resetVideo.emit();
	}

	selectLeaderMediaclip(mediaclipId: string) {
		if (this.playout) {
			const position: Position = {
				type: 'leader',
				name: this.playout.label + '_leader',
				system: 'msas',
				'media-types': ['video', 'audio'],
				systems: []
			};

			if (mediaclipId) {
				const system: System = {
					type: 'vms',
					subtype: 'generic',
					priority: '1',
					label: 'Creative',
					systemconfig: {
						'media-clipid': mediaclipId
					}
				};

				position.systems = [system];

				this.leaderPositionId = mediaclipId;
			} else {
				this.leaderPositionId = null;
			}
			this.playout.positions = [position];
			if (this.initialLeaderPositionId !== this.leaderPositionId) {
				this.form.form.markAsDirty();
			}
		}
	}

	getLeaderMediaclipId(positions: any): string {
		let mediaclipId: string = null;
		if (positions) {
			positions.forEach((position: Position) => {
				if (position && position.type) {
					if (position.type === 'leader') {
						if (position.systems && position.systems.length > 0) {
							position.systems.forEach((system: System) => {
								if (system && system.systemconfig) {
									mediaclipId = system.systemconfig['media-clipid'];
								}
							});
						}
					}
				}
			});
		}
		return mediaclipId;
	}

	changePlayoutLeaderLogo(leaderPositionId: string) {
		this.selectLeaderMediaclip(leaderPositionId);
	}

	removeLeaderMediaclip() {
		if (this.playout) {
			this.leaderPositionId = null;
			this.selectLeaderMediaclip(null);
			this.form.form.markAsDirty();
		}
	}

	private setLanguageOptions() {
		this.defaultSubtitleOptions = [
			{
				label: this.translate.instant('FIRST_SUBTITLE_OF_CLIP'),
				value: 'First Available Subtitle'
			},
			{
				label: this.translate.instant('DEFAULT_LANGUAGE', { defaultLanguage: this.defaultLanguage }),
				value: 'Default Language'
			},
			{
				label: this.translate.instant('NONE'),
				value: 'None'
			}
		];

		this.defaultAudiotrackOptions = [
			{
				label: this.translate.instant('DEFAULT_LANGUAGE', { defaultLanguage: this.defaultLanguage }),
				value: 'Default Language'
			},
			{
				label: this.translate.instant('FIRST_AUDIO_TRACK_OF_CLIP'),
				value: 'First Available Audio Track'
			},
			{
				label: this.translate.instant('ORIGINAL'),
				value: 'Main'
			}
		];
	}
}
